import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";

const API_URL = "api/v1/ts-candidate-screen/";

function getAuthToken() {
  return (
    (typeof window !== "undefined" &&
      window.localStorage.getItem("authToken")) ||
    ""
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  const code = parseInt(status, 10);
  const ok = code >= 200 && code < 300;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        background: ok ? "#ecfdf3" : "#fef3f2",
        color: ok ? "#027a48" : "#b42318",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: ok ? "#12b76a" : "#f04438",
        }}
      />
      {status || "—"}
    </span>
  );
}

function ExpandedRow({ data }) {
  let payload = data.payload;
  let responseData = data.response_data;

  try {
    if (typeof payload === "string") {
      payload = JSON.parse(payload.replace(/'/g, '"'));
    }
  } catch {}

  return (
    <div style={styles.expandWrap}>
      <div style={styles.expandCol}>
        <div style={styles.expandLabel}>Payload</div>
        <pre style={styles.expandPre}>
          {typeof payload === "object"
            ? JSON.stringify(payload, null, 2)
            : String(payload || "—")}
        </pre>
      </div>

      <div style={styles.expandCol}>
        <div style={styles.expandLabel}>Response</div>
        <pre style={styles.expandPre}>
          {responseData
            ? JSON.stringify(responseData, null, 2)
            : "—"}
        </pre>

        {data.remarks && (
          <>
            <div style={{ ...styles.expandLabel, marginTop: 10 }}>
              Remarks
            </div>
            <div>{data.remarks}</div>
          </>
        )}
      </div>
    </div>
  );
}

const columns = [
  { name: "ID", selector: (row) => row.id, sortable: true, width: "80px" },
  {
    name: "Job ID",
    selector: (row) => row.job,
    sortable: true,
    width: "100px",
  },
  {
    name: "Candidate ID",
    selector: (row) => row.candidate,
    sortable: true,
    width: "130px",
  },
  {
    name: "Message",
    selector: (row) => row.response_data?.message || "—",
    grow: 2,
    wrap: true,
  },
  {
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
    width: "120px",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    name: "Created At",
    selector: (row) => row.created_at,
    sortable: true,
    width: "190px",
    cell: (row) => formatDate(row.created_at),
  },
];

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#0b2a4a",
      minHeight: "46px",
    },
  },
  headCells: {
    style: {
      color: "#fff",
      fontSize: "12px",
      fontWeight: 600,
    },
  },
};

export default function TsCandidateScreenLogs() {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchLogs = useCallback(async (pageNum, size) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      const res = await axios.get(API_URL, {
        params: { page: pageNum, page_size: size },
      });

      setRows(res.data.results || []);
      setTotalRows(res.data.count || 0);
    } catch (err) {
      setError("Failed to fetch data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page, pageSize);
  }, [page, pageSize, fetchLogs]);

  const filteredRows = rows
    .filter((row) => {
      if (statusFilter === "all") return true;
      const code = parseInt(row.status, 10);
      const ok = code >= 200 && code < 300;
      return statusFilter === "success" ? ok : !ok;
    })
    .filter((row) => {
      if (!search) return true;
      return JSON.stringify(row)
        .toLowerCase()
        .includes(search.toLowerCase());
    });

  return (
    <div style={styles.page}>
      <h2>Screening Logs</h2>

      <div style={styles.controls}>
        <input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="all">All</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>

        <button onClick={() => fetchLogs(page, pageSize)} style={styles.button}>
          Refresh
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.card}>
        <DataTable
          columns={columns}
          data={filteredRows}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(r) => setPageSize(r)}
          customStyles={customStyles}
          expandableRows
          expandableRowsComponent={ExpandedRow}
          striped
        />
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 24, fontFamily: "sans-serif" },
  controls: { display: "flex", gap: 10, marginBottom: 10 },
  input: {
    padding: 6,
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  select: { padding: 6, borderRadius: 6 },
  button: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #ccc",
    cursor: "pointer",
  },
  error: { color: "red", marginBottom: 10 },
  card: {
    border: "1px solid #eee",
    borderRadius: 8,
    overflow: "hidden",
  },
  expandWrap: {
    display: "flex",
    gap: 20,
    padding: 16,
    background: "#f8fafc",
  },
  expandCol: { flex: 1 },
  expandLabel: { fontSize: 12, fontWeight: 600 },
  expandPre: {
    fontSize: 12,
    background: "#0b2a4a",
    color: "#fff",
    padding: 10,
    borderRadius: 6,
  },
};